import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

type TabType = 'novo' | 'meus' | 'chat' | 'faq' | 'treinamento';

interface TabBadgeProps {
  count: number;
  tooltip: string;
  active: boolean;
}

function TabBadge({ count, tooltip, active }: TabBadgeProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  if (count === 0) return null;
  return (
    <span
      className="relative ml-1.5 inline-flex items-center"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className={`min-w-[20px] h-5 rounded-full flex items-center justify-center text-xs font-bold px-1.5 leading-none cursor-default ${
        active ? 'bg-white text-teal-600' : 'bg-red-500 text-white'
      }`}>
        {count > 99 ? '99+' : count}
      </span>
      {showTooltip && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-slate-900 text-white text-xs rounded-lg whitespace-nowrap z-50 pointer-events-none">
          {tooltip}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></span>
        </span>
      )}
    </span>
  );
}

interface Ticket {
  id: string;
  assunto: string;
  categoria: string;
  status: 'aberto' | 'em_andamento' | 'resolvido' | 'fechado';
  prioridade: 'baixa' | 'media' | 'alta' | 'urgente';
  dataCriacao: string;
  dataAtualizacao: string;
  descricao: string;
}

interface ChatMessage {
  id: string;
  tipo: 'usuario' | 'atendente' | 'sistema';
  mensagem: string;
  hora: string;
  nomeAtendente?: string;
}

export default function SuportePage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'novo' | 'meus' | 'chat' | 'faq' | 'treinamento'>('chat');
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  
  // Form data state
  const [formData, setFormData] = useState({
    categoria: '',
    assunto: '',
    descricao: '',
    prioridade: 'media'
  });

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      tipo: 'sistema',
      mensagem: 'Bem-vindo ao chat de suporte! Como podemos ajudá-lo hoje?',
      hora: '14:30'
    },
    {
      id: '2',
      tipo: 'atendente',
      mensagem: 'Olá! Sou a Ana do suporte. Estou aqui para ajudar você. Qual é a sua dúvida?',
      hora: '14:31',
      nomeAtendente: 'Ana Silva'
    }
  ]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [atendenteOnline, setAtendenteOnline] = useState(true);

  const [tickets] = useState<Ticket[]>([
    {
      id: 'TKT-001',
      assunto: 'Problema ao resgatar recompensa',
      categoria: 'Recompensas',
      status: 'em_andamento',
      prioridade: 'alta',
      dataCriacao: '2024-01-15',
      dataAtualizacao: '2024-01-16',
      descricao: 'Não consigo resgatar minha recompensa de 500 pontos'
    },
    {
      id: 'TKT-002',
      assunto: 'Dúvida sobre pontos',
      categoria: 'Pontos',
      status: 'resolvido',
      prioridade: 'media',
      dataCriacao: '2024-01-10',
      dataAtualizacao: '2024-01-12',
      descricao: 'Gostaria de saber como ganhar mais pontos'
    }
  ]);

  const categorias = [
    { value: 'tecnico', label: 'Problema Técnico' },
    { value: 'pontos', label: 'Pontos e XP' },
    { value: 'recompensas', label: 'Recompensas' },
    { value: 'missoes', label: 'Missões' },
    { value: 'conta', label: 'Minha Conta' },
    { value: 'outro', label: 'Outro' }
  ];

  const faqItems = [
    {
      categoria: 'Pontos e XP',
      perguntas: [
        {
          pergunta: 'Como ganho pontos?',
          resposta: 'Você ganha pontos completando missões, participando de jogos, atingindo objetivos e interagindo com a plataforma.'
        },
        {
          pergunta: 'Os pontos expiram?',
          resposta: 'Depende da campanha. Algumas campanhas têm pontos com validade, outras não. Verifique os detalhes da sua campanha.'
        }
      ]
    },
    {
      categoria: 'Recompensas',
      perguntas: [
        {
          pergunta: 'Como resgato uma recompensa?',
          resposta: 'Acesse a página de Recompensas, escolha o item desejado e clique em "Resgatar". Você receberá um email com as instruções.'
        },
        {
          pergunta: 'Quanto tempo leva para receber minha recompensa?',
          resposta: 'Recompensas digitais são entregues em até 24 horas. Recompensas físicas podem levar de 7 a 15 dias úteis.'
        }
      ]
    },
    {
      categoria: 'Missões',
      perguntas: [
        {
          pergunta: 'Como completo uma missão?',
          resposta: 'Cada missão tem objetivos específicos. Acesse a página de Missões para ver os detalhes e o progresso de cada uma.'
        },
        {
          pergunta: 'Posso fazer várias missões ao mesmo tempo?',
          resposta: 'Sim! Você pode participar de quantas missões quiser simultaneamente.'
        }
      ]
    },
    {
      categoria: 'Níveis e Distintivos',
      perguntas: [
        {
          pergunta: 'Como subo de nível?',
          resposta: 'Você sobe de nível acumulando XP através de missões, jogos e outras atividades na plataforma.'
        },
        {
          pergunta: 'O que são distintivos?',
          resposta: 'Distintivos são conquistas especiais que você ganha ao completar desafios específicos ou atingir marcos importantes.'
        }
      ]
    }
  ];

  const videosCategories = [
    {
      categoria: 'Primeiros Passos',
      videos: [
        {
          titulo: 'Bem-vindo à Plataforma',
          descricao: 'Conheça a plataforma e suas principais funcionalidades',
          duracao: '5:30',
          thumbnail: 'https://readdy.ai/api/search-image?query=modern%20digital%20platform%20welcome%20screen%20with%20clean%20interface%20showing%20dashboard%20overview%20professional%20business%20technology%20minimalist%20design%20teal%20accents%20bright%20background&width=400&height=225&seq=video1&orientation=landscape',
          url: '#',
          watched: true
        },
        {
          titulo: 'Como Configurar seu Perfil',
          descricao: 'Aprenda a personalizar seu perfil e configurações',
          duracao: '4:15',
          thumbnail: 'https://readdy.ai/api/search-image?query=user%20profile%20settings%20configuration%20screen%20modern%20interface%20with%20avatar%20customization%20options%20clean%20professional%20design%20teal%20color%20scheme%20bright%20simple%20background&width=400&height=225&seq=video2&orientation=landscape',
          url: '#',
          watched: true
        },
        {
          titulo: 'Navegando pela Plataforma',
          descricao: 'Tour completo pelas principais áreas e menus',
          duracao: '6:45',
          thumbnail: 'https://readdy.ai/api/search-image?query=platform%20navigation%20menu%20interface%20showing%20multiple%20sections%20modern%20dashboard%20layout%20clean%20professional%20design%20teal%20highlights%20bright%20minimalist%20background&width=400&height=225&seq=video3&orientation=landscape',
          url: '#',
          watched: false
        }
      ]
    },
    {
      categoria: 'Pontos e Recompensas',
      videos: [
        {
          titulo: 'Como Ganhar Pontos',
          descricao: 'Descubra todas as formas de acumular pontos',
          duracao: '7:20',
          thumbnail: 'https://readdy.ai/api/search-image?query=points%20rewards%20system%20digital%20coins%20accumulation%20modern%20gamification%20interface%20clean%20design%20teal%20gold%20accents%20bright%20simple%20background&width=400&height=225&seq=video4&orientation=landscape',
          url: '#',
          watched: true
        },
        {
          titulo: 'Resgatando Recompensas',
          descricao: 'Passo a passo para resgatar suas recompensas',
          duracao: '5:50',
          thumbnail: 'https://readdy.ai/api/search-image?query=rewards%20redemption%20process%20gift%20boxes%20prizes%20modern%20interface%20clean%20professional%20design%20teal%20accents%20bright%20minimalist%20background&width=400&height=225&seq=video5&orientation=landscape',
          url: '#',
          watched: true
        },
        {
          titulo: 'Catálogo de Recompensas',
          descricao: 'Conheça todas as recompensas disponíveis',
          duracao: '8:10',
          thumbnail: 'https://readdy.ai/api/search-image?query=rewards%20catalog%20showcase%20various%20prizes%20products%20modern%20grid%20layout%20clean%20professional%20design%20teal%20color%20scheme%20bright%20simple%20background&width=400&height=225&seq=video6&orientation=landscape',
          url: '#',
          watched: false
        }
      ]
    },
    {
      categoria: 'Missões e Desafios',
      videos: [
        {
          titulo: 'Completando Missões',
          descricao: 'Como participar e completar missões',
          duracao: '6:30',
          thumbnail: 'https://readdy.ai/api/search-image?query=mission%20completion%20checklist%20tasks%20achievement%20modern%20interface%20clean%20design%20teal%20accents%20bright%20simple%20background&width=400&height=225&seq=video7&orientation=landscape',
          url: '#',
          watched: true
        },
        {
          titulo: 'Desafios Diários',
          descricao: 'Maximize seus ganhos com desafios diários',
          duracao: '4:40',
          thumbnail: 'https://readdy.ai/api/search-image?query=daily%20challenges%20calendar%20tasks%20modern%20gamification%20interface%20clean%20professional%20design%20teal%20highlights%20bright%20simple%20background&width=400&height=225&seq=video8&orientation=landscape',
          url: '#',
          watched: true
        },
        {
          titulo: 'Sequência de Dias (Streak)',
          descricao: 'Entenda o sistema de sequência e bônus',
          duracao: '5:15',
          thumbnail: 'https://readdy.ai/api/search-image?query=streak%20counter%20consecutive%20days%20achievement%20fire%20icon%20modern%20interface%20clean%20design%20teal%20orange%20accents%20bright%20minimalist%20background&width=400&height=225&seq=video9&orientation=landscape',
          url: '#',
          watched: false
        }
      ]
    },
    {
      categoria: 'Gamificação',
      videos: [
        {
          titulo: 'Sistema de Níveis',
          descricao: 'Como funciona a progressão de níveis',
          duracao: '7:00',
          thumbnail: 'https://readdy.ai/api/search-image?query=level%20progression%20system%20experience%20bar%20ranking%20modern%20gamification%20interface%20clean%20design%20teal%20accents%20bright%20simple%20background&width=400&height=225&seq=video10&orientation=landscape',
          url: '#',
          watched: false
        },
        {
          titulo: 'Conquistando Distintivos',
          descricao: 'Conheça os distintivos e como conquistá-los',
          duracao: '6:20',
          thumbnail: 'https://readdy.ai/api/search-image?query=achievement%20badges%20collection%20medals%20trophies%20modern%20interface%20clean%20professional%20design%20teal%20gold%20accents%20bright%20minimalist%20background&width=400&height=225&seq=video11&orientation=landscape',
          url: '#',
          watched: false
        },
        {
          titulo: 'Ranking e Competições',
          descricao: 'Participe do ranking e compete com outros',
          duracao: '5:45',
          thumbnail: 'https://readdy.ai/api/search-image?query=leaderboard%20ranking%20competition%20podium%20modern%20interface%20clean%20professional%20design%20teal%20accents%20bright%20simple%20background&width=400&height=225&seq=video12&orientation=landscape',
          url: '#',
          watched: false
        }
      ]
    },
    {
      categoria: 'Dicas e Truques',
      videos: [
        {
          titulo: 'Maximizando seus Pontos',
          descricao: 'Estratégias para ganhar mais pontos',
          duracao: '8:30',
          thumbnail: 'https://readdy.ai/api/search-image?query=strategy%20tips%20growth%20chart%20upward%20trend%20modern%20interface%20clean%20professional%20design%20teal%20accents%20bright%20minimalist%20background&width=400&height=225&seq=video13&orientation=landscape',
          url: '#',
          watched: false
        },
        {
          titulo: 'Melhores Práticas',
          descricao: 'Dicas dos usuários mais bem-sucedidos',
          duracao: '7:15',
          thumbnail: 'https://readdy.ai/api/search-image?query=best%20practices%20checklist%20success%20tips%20modern%20interface%20clean%20professional%20design%20teal%20accents%20bright%20simple%20background&width=400&height=225&seq=video14&orientation=landscape',
          url: '#',
          watched: false
        },
        {
          titulo: 'Perguntas Mais Comuns',
          descricao: 'Respostas para as dúvidas frequentes',
          duracao: '9:00',
          thumbnail: 'https://readdy.ai/api/search-image?query=frequently%20asked%20questions%20help%20support%20modern%20interface%20clean%20professional%20design%20teal%20accents%20bright%20minimalist%20background&width=400&height=225&seq=video15&orientation=landscape',
          url: '#',
          watched: false
        }
      ]
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Chamado criado com sucesso! Você receberá uma resposta em breve.');
    setFormData({
      categoria: '',
      assunto: '',
      descricao: '',
      prioridade: 'media'
    });
  };

  const handleEnviarMensagem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!novaMensagem.trim()) return;

    const agora = new Date();
    const hora = `${agora.getHours()}:${agora.getMinutes().toString().padStart(2, '0')}`;

    const novaMensagemObj: ChatMessage = {
      id: Date.now().toString(),
      tipo: 'usuario',
      mensagem: novaMensagem,
      hora
    };

    setChatMessages([...chatMessages, novaMensagemObj]);
    setNovaMensagem('');

    // Simular resposta do atendente após 2 segundos
    setTimeout(() => {
      const respostaAtendente: ChatMessage = {
        id: (Date.now() + 1).toString(),
        tipo: 'atendente',
        mensagem: 'Obrigado pela sua mensagem! Estou analisando sua solicitação e já vou te responder.',
        hora: `${agora.getHours()}:${(agora.getMinutes() + 1).toString().padStart(2, '0')}`,
        nomeAtendente: 'Ana Silva'
      };
      setChatMessages(prev => [...prev, respostaAtendente]);
    }, 2000);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      aberto: 'bg-blue-100 text-blue-800',
      em_andamento: 'bg-yellow-100 text-yellow-800',
      resolvido: 'bg-green-100 text-green-800',
      fechado: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || colors.aberto;
  };

  const getPrioridadeColor = (prioridade: string) => {
    const colors = {
      baixa: 'bg-gray-100 text-gray-800',
      media: 'bg-blue-100 text-blue-800',
      alta: 'bg-orange-100 text-orange-800',
      urgente: 'bg-red-100 text-red-800'
    };
    return colors[prioridade as keyof typeof colors] || colors.media;
  };

  const handleVideoClick = (video: any) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  };

  // Contadores para badges das abas
  const ticketsAbertos = tickets.filter(t => t.status === 'aberto' || t.status === 'em_andamento').length;
  const chatNaoLidas = chatMessages.filter(m => m.tipo === 'atendente').length;
  const videosNaoAssistidos = videosCategories.reduce((acc, cat) => acc + cat.videos.filter(v => !v.watched).length, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Central de Suporte</h1>
          <p className="text-gray-600">Estamos aqui para ajudar você!</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6 p-1">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('novo')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all whitespace-nowrap flex items-center justify-center ${
                activeTab === 'novo'
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-add-circle-line mr-2"></i>
              Novo Chamado
            </button>
            <button
              onClick={() => setActiveTab('meus')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all whitespace-nowrap flex items-center justify-center ${
                activeTab === 'meus'
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-file-list-3-line mr-2"></i>
              Meus Chamados
              <TabBadge
                count={ticketsAbertos}
                tooltip={`${ticketsAbertos} chamado${ticketsAbertos !== 1 ? 's' : ''} em aberto`}
                active={activeTab === 'meus'}
              />
            </button>
            <button
              onClick={() => setActiveTab('chat')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all whitespace-nowrap flex items-center justify-center relative ${
                activeTab === 'chat'
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-message-3-line mr-2"></i>
              Chat ao Vivo
              <TabBadge
                count={chatNaoLidas}
                tooltip={`${chatNaoLidas} mensagem${chatNaoLidas !== 1 ? 'ns' : ''} não lida${chatNaoLidas !== 1 ? 's' : ''}`}
                active={activeTab === 'chat'}
              />
              {atendenteOnline && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-green-500 rounded-full"></span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('faq')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all whitespace-nowrap flex items-center justify-center ${
                activeTab === 'faq'
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-question-line mr-2"></i>
              Perguntas Frequentes
            </button>
            <button
              onClick={() => setActiveTab('treinamento')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all whitespace-nowrap flex items-center justify-center ${
                activeTab === 'treinamento'
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-play-circle-line mr-2"></i>
              Vídeos
              <TabBadge
                count={videosNaoAssistidos}
                tooltip={`${videosNaoAssistidos} vídeo${videosNaoAssistidos !== 1 ? 's' : ''} não assistido${videosNaoAssistidos !== 1 ? 's' : ''}`}
                active={activeTab === 'treinamento'}
              />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          {/* Novo Chamado */}
          {activeTab === 'novo' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-6">Abrir Novo Chamado</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categoria
                  </label>
                  <select
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  >
                    <option value="">Selecione uma categoria</option>
                    {categorias.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assunto
                  </label>
                  <input
                    type="text"
                    value={formData.assunto}
                    onChange={(e) => setFormData({ ...formData, assunto: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Descreva brevemente o problema"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição Detalhada
                  </label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                    placeholder="Descreva seu problema em detalhes..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prioridade
                  </label>
                  <select
                    value={formData.prioridade}
                    onChange={(e) => setFormData({ ...formData, prioridade: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="baixa">Baixa</option>
                    <option value="media">Média</option>
                    <option value="alta">Alta</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>

                <button
                  type="submit"
                  className="w-full bg-teal-500 text-white py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-send-plane-fill mr-2"></i>
                  Enviar Chamado
                </button>
              </form>
            </div>
          )}

          {/* Meus Chamados */}
          {activeTab === 'meus' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Meus Chamados</h2>
                <button
                  onClick={() => setActiveTab('novo')}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-add-circle-line"></i>
                  Novo Chamado
                </button>
              </div>
              <div className="space-y-4">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm text-gray-500">{ticket.id}</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                            {ticket.status.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPrioridadeColor(ticket.prioridade)}`}>
                            {ticket.prioridade.toUpperCase()}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">{ticket.assunto}</h3>
                        <p className="text-sm text-gray-600 mb-2">{ticket.descricao}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>
                            <i className="ri-folder-line mr-1"></i>
                            {ticket.categoria}
                          </span>
                          <span>
                            <i className="ri-calendar-line mr-1"></i>
                            Criado em {new Date(ticket.dataCriacao).toLocaleDateString('pt-BR')}
                          </span>
                          <span>
                            <i className="ri-time-line mr-1"></i>
                            Atualizado em {new Date(ticket.dataAtualizacao).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                      <button className="text-teal-500 hover:text-teal-600 whitespace-nowrap">
                        <i className="ri-arrow-right-line text-xl"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Chat ao Vivo */}
          {activeTab === 'chat' && (
            <div className="flex flex-col h-[600px]">
              {/* Chat Header */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                      <i className="ri-customer-service-2-fill text-white text-xl"></i>
                    </div>
                    {atendenteOnline && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Suporte ao Vivo</h3>
                    <p className="text-sm text-gray-600">
                      {atendenteOnline ? (
                        <span className="text-green-600">
                          <i className="ri-checkbox-blank-circle-fill text-xs mr-1"></i>
                          Online - Tempo médio de resposta: 2 min
                        </span>
                      ) : (
                        <span className="text-gray-500">
                          <i className="ri-checkbox-blank-circle-fill text-xs mr-1"></i>
                          Offline - Deixe sua mensagem
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-600">
                  <i className="ri-more-2-fill text-xl"></i>
                </button>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
                {chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.tipo === 'usuario' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${msg.tipo === 'usuario' ? 'order-2' : 'order-1'}`}>
                      {msg.tipo === 'atendente' && (
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                            <i className="ri-customer-service-2-fill text-white text-xs"></i>
                          </div>
                          <span className="text-xs font-medium text-gray-700">{msg.nomeAtendente}</span>
                        </div>
                      )}
                      <div
                        className={`rounded-2xl px-4 py-3 ${
                          msg.tipo === 'usuario'
                            ? 'bg-teal-500 text-white rounded-br-none'
                            : msg.tipo === 'sistema'
                            ? 'bg-gray-100 text-gray-600 text-center text-sm'
                            : 'bg-gray-100 text-gray-900 rounded-bl-none'
                        }`}
                      >
                        <p className="text-sm leading-relaxed">{msg.mensagem}</p>
                      </div>
                      <span className={`text-xs text-gray-500 mt-1 block ${msg.tipo === 'usuario' ? 'text-right' : 'text-left'}`}>
                        {msg.hora}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Chat Input */}
              <form onSubmit={handleEnviarMensagem} className="border-t border-gray-200 pt-4">
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <i className="ri-attachment-2 text-xl"></i>
                  </button>
                  <input
                    type="text"
                    value={novaMensagem}
                    onChange={(e) => setNovaMensagem(e.target.value)}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <i className="ri-emotion-line text-xl"></i>
                  </button>
                  <button
                    type="submit"
                    disabled={!novaMensagem.trim()}
                    className="px-6 py-3 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <i className="ri-send-plane-fill mr-2"></i>
                    Enviar
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* FAQ */}
          {activeTab === 'faq' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-6">Perguntas Frequentes</h2>
              <div className="space-y-6">
                {faqItems.map((categoria, idx) => (
                  <div key={idx}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-folder-line mr-2 text-teal-500"></i>
                      {categoria.categoria}
                    </h3>
                    <div className="space-y-3">
                      {categoria.perguntas.map((item, qIdx) => (
                        <details
                          key={qIdx}
                          className="group border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                            <span className="font-medium text-gray-900">{item.pergunta}</span>
                            <i className="ri-arrow-down-s-line text-xl text-gray-400 group-open:rotate-180 transition-transform"></i>
                          </summary>
                          <div className="px-4 pb-4 text-gray-600 border-t border-gray-100 pt-4">
                            {item.resposta}
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Contato Direto */}
              <div className="mt-8 p-6 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Não encontrou sua resposta?
                </h3>
                <p className="text-gray-600 mb-4">
                  Entre em contato diretamente com nossa equipe de suporte:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <a
                    href="mailto:suporte@exemplo.com"
                    className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                      <i className="ri-mail-line text-white text-xl"></i>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Email</div>
                      <div className="text-sm text-gray-600">suporte@exemplo.com</div>
                    </div>
                  </a>
                  <a
                    href="https://wa.me/5511999999999"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                      <i className="ri-whatsapp-line text-white text-xl"></i>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">WhatsApp</div>
                      <div className="text-sm text-gray-600">(11) 99999-9999</div>
                    </div>
                  </a>
                  <a
                    href="tel:+551140004000"
                    className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                      <i className="ri-phone-line text-white text-xl"></i>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Telefone</div>
                      <div className="text-sm text-gray-600">(11) 4000-4000</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Treinamento */}
          {activeTab === 'treinamento' && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Vídeos de Suporte</h2>
                <p className="text-gray-600">Aprenda a usar todas as funcionalidades da plataforma</p>
              </div>

              <div className="space-y-8">
                {videosCategories.map((categoria, idx) => (
                  <div key={idx}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-folder-video-line mr-2 text-teal-500"></i>
                      {categoria.categoria}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {categoria.videos.map((video, vIdx) => (
                        <div
                          key={vIdx}
                          onClick={() => handleVideoClick(video)}
                          className="group border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all cursor-pointer"
                        >
                          {/* Thumbnail */}
                          <div className="relative aspect-video bg-gray-100 overflow-hidden">
                            <img
                              src={video.thumbnail}
                              alt={video.titulo}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                              <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                <i className="ri-play-fill text-3xl text-teal-500 ml-1"></i>
                              </div>
                            </div>
                            <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                              {video.duracao}
                            </div>
                            {video.watched && (
                              <div className="absolute top-2 left-2 bg-teal-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                                <i className="ri-check-line"></i>
                                Assistido
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="p-4">
                            <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-teal-500 transition-colors">
                              {video.titulo}
                            </h4>
                            <p className="text-sm text-gray-600 line-clamp-2">
                              {video.descricao}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Video Modal */}
      {isVideoModalOpen && selectedVideo && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-blue-500 p-6 text-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2">{selectedVideo.titulo}</h3>
                  <p className="text-white/90">{selectedVideo.descricao}</p>
                </div>
                <button
                  onClick={() => setIsVideoModalOpen(false)}
                  className="ml-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            {/* Video Player */}
            <div className="relative aspect-video bg-black">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-play-fill text-5xl ml-1"></i>
                  </div>
                  <p className="text-lg mb-2">Vídeo de Treinamento</p>
                  <p className="text-sm text-white/70">Duração: {selectedVideo.duracao}</p>
                </div>
              </div>
            </div>

            {/* Video Info */}
            <div className="p-6 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <i className="ri-time-line"></i>
                    <span className="text-sm">{selectedVideo.duracao}</span>
                  </div>
                  {selectedVideo.watched && (
                    <div className="flex items-center gap-2 text-teal-600">
                      <i className="ri-checkbox-circle-line"></i>
                      <span className="text-sm font-medium">Já assistido</span>
                    </div>
                  )}
                </div>
                <button className="px-6 py-2 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-xl hover:shadow-lg transition-all whitespace-nowrap">
                  <i className="ri-check-line mr-2"></i>
                  Marcar como Assistido
                </button>
              </div>

              {/* Related Videos */}
              <div className="border-t pt-4">
                <h4 className="font-bold text-gray-900 mb-3">Próximos Vídeos Recomendados</h4>
                <div className="grid grid-cols-3 gap-4">
                  {videosCategories[0].videos.slice(0, 3).map((video, idx) => (
                    <div 
                      key={idx}
                      className="bg-white rounded-xl p-3 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => {
                        setSelectedVideo(video);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-play-fill text-xl text-teal-500"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-sm text-gray-900 truncate">{video.titulo}</h5>
                          <p className="text-xs text-gray-500">{video.duracao}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}